import { Box, Stack, Typography } from "@mui/material"
import { useQueryAdminDashboardStoreItems } from "api/reactQuery/queries/adminDashboard"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import { IDashboardPeriodParams } from "ts/interfaces/AdminDashboard"

import DashboardSection from "./DashboardSection"
import DashboardSectionBody, {
  RowsSkeleton,
  useSectionState,
} from "./DashboardSectionBody"
import RankedBarRow from "./RankedBarRow"

const MAX_ROWS = 10

const formatAmount = (amount: number) =>
  `$${amount.toLocaleString("en-US", { maximumFractionDigits: 2 })}`

const StoreItems = ({ params }: { params: IDashboardPeriodParams }) => {
  const { t } = useTranslation()
  const query = useQueryAdminDashboardStoreItems(params)

  // BED has no limit param, so the cap is FE-side.
  const rows = query.data?.data.rows.slice(0, MAX_ROWS) ?? []
  const top = rows[0]?.purchase_count ?? 0
  const { state } = useSectionState([query], !rows.length)
  const purchasesHeader = t("adminDashboard.purchasesColumn")
  const spentHeader = t("adminDashboard.spentColumn")
  // One width per column on every row, so bars end together under their headers.
  const columnWidth = (header: string, values: string[]) =>
    `${Math.max(header.length, ...values.map((value) => value.length))}ch`
  const purchasesWidth = columnWidth(
    purchasesHeader,
    rows.map((row) => row.purchase_count.toLocaleString("en-US")),
  )
  const spentWidth = columnWidth(
    spentHeader,
    rows.map((row) => formatAmount(row.purchase_amount)),
  )
  const cellSx = (width: string) => ({
    width,
    flexShrink: 0,
    textAlign: "right",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  })
  // 14px `ch` box, right-anchored so a wider header grows into the gap.
  const headerCellSx = (width: string) => ({
    width,
    flexShrink: 0,
    display: "flex",
    justifyContent: "flex-end",
    whiteSpace: "nowrap",
    fontSize: "14px",
  })

  return (
    <DashboardSection
      title={t("adminDashboard.storeItems")}
      question={t("adminDashboard.storeItemsQuestion")}
      period={query.data?.data}
      exportImage={{
        section: "store-items",
        disabled: state !== "ready",
      }}
    >
      <DashboardSectionBody
        queries={[query]}
        isEmpty={!rows.length}
        emptyText={t("adminDashboard.storeItemsEmpty")}
        skeleton={<RowsSkeleton rows={MAX_ROWS} />}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          columnGap={2}
          sx={{
            pt: "10px",
            color: "mockup.neutral30",
            textTransform: "uppercase",
          }}
        >
          <Typography variant="body3" sx={{ pl: 5 }}>
            {t("adminDashboard.itemColumn")}
          </Typography>
          <Stack direction="row" gap={2}>
            <Box sx={headerCellSx(purchasesWidth)}>
              <Typography variant="body3">{purchasesHeader}</Typography>
            </Box>
            <Box sx={headerCellSx(spentWidth)}>
              <Typography variant="body3">{spentHeader}</Typography>
            </Box>
          </Stack>
        </Stack>
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
          {rows.map((row, index) => {
            const count = row.purchase_count.toLocaleString("en-US")
            const amount = formatAmount(row.purchase_amount)
            return (
              <RankedBarRow
                key={row.fk_store_item_id ?? `${row.article_name}-${index}`}
                rank={index + 1}
                label={row.article_name}
                labelColor="mockup.neutral10"
                labelWeight={500}
                ratio={top > 0 ? row.purchase_count / top : 0}
                barColor="warning.main"
                right={
                  <Stack direction="row" gap={2}>
                    <Typography
                      aria-hidden="true"
                      variant="body2"
                      color="mockup.secondary30"
                      sx={cellSx(purchasesWidth)}
                    >
                      {count}
                    </Typography>
                    <Typography
                      aria-hidden="true"
                      variant="body2"
                      color="mockup.secondary30"
                      sx={cellSx(spentWidth)}
                    >
                      {amount}
                    </Typography>
                    <Box component="span" sx={visuallyHiddenSx}>
                      {t("adminDashboard.storeItemRowSummary", {
                        purchases: count,
                        amount,
                      })}
                    </Box>
                  </Stack>
                }
              />
            )
          })}
        </Box>
      </DashboardSectionBody>
    </DashboardSection>
  )
}

export default StoreItems
